import React from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { trainingService } from '../../services/trainingService'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'

const trainingSchema = z.object({
  titel: z.string().min(1, 'Titel är obligatorisk'),
  beskrivning: z.string().optional(),
  datum: z.string().min(1, 'Datum och tid är obligatoriska'),
  location: z.string().optional(),
  max_platser: z.number().min(0).optional(),
  bootcamp: z.boolean(),
  bootcamp_id: z.number().optional(),
  free_training: z.boolean(),
  allow_non_bootcamp_join: z.boolean(),
})

type TrainingFormData = z.infer<typeof trainingSchema>

interface TrainingFormProps {
  onSuccess?: () => void
  onCancel?: () => void
}

export const TrainingForm: React.FC<TrainingFormProps> = ({ onSuccess, onCancel }) => {
  const queryClient = useQueryClient()

  const createTrainingMutation = useMutation({
    mutationFn: trainingService.createTraining,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trainings'] })
      toast.success('Träning skapad framgångsrikt!')
      onSuccess?.()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Misslyckades att skapa träning')
    },
  })

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<TrainingFormData>({
    resolver: zodResolver(trainingSchema),
    defaultValues: {
      bootcamp: false,
      free_training: false,
      allow_non_bootcamp_join: true,
    },
  })

  const bootcampEnabled = watch('bootcamp')

  const onSubmit = async (data: TrainingFormData) => {
    try {
      await createTrainingMutation.mutateAsync(data)
    } catch (error) {
      // Error is handled by the mutation
    }
  }

  // Set default date/time to next available slot
  React.useEffect(() => {
    const now = new Date()
    const day = now.getDay()
    // Default to evening training (18:30) for weekdays, morning (09:00) for weekends
    const hour = day === 0 || day === 6 ? 9 : 18
    const minute = day === 0 || day === 6 ? 0 : 30

    now.setHours(hour, minute, 0, 0)

    // If it's already past the default time today, move to tomorrow
    if (now <= new Date()) {
      now.setDate(now.getDate() + 1)
    }

    const defaultDateTime = now.toISOString().slice(0, 16)
    setValue('datum', defaultDateTime)
  }, [setValue])

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Title */}
        <div className="md:col-span-2">
          <Input
            label="Titel"
            {...register('titel')}
            error={errors.titel?.message}
          />
        </div>

        {/* Date & Time */}
        <div>
          <Input
            label="Datum och tid"
            type="datetime-local"
            {...register('datum')}
            error={errors.datum?.message}
          />
        </div>

        {/* Location */}
        <div>
          <Input
            label="Plats"
            placeholder="t.ex. Centralgatan 12"
            {...register('location')}
            error={errors.location?.message}
          />
        </div>

        {/* Max Participants */}
        <div>
          <Input
            label="Max antal platser"
            type="number"
            min="0"
            placeholder="Lämna tomt för obegränsat"
            {...register('max_platser', { valueAsNumber: true })}
            error={errors.max_platser?.message}
          />
        </div>

        {/* Description */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Beskrivning
          </label>
          <textarea
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            rows={3}
            placeholder="Beskriv träningen..."
            {...register('beskrivning')}
          />
        </div>
      </div>

      {/* Checkboxes */}
      <div className="space-y-4">
        {/* Free Training */}
        <div className="flex items-center">
          <input
            type="checkbox"
            id="free_training"
            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            {...register('free_training')}
          />
          <label htmlFor="free_training" className="ml-2 block text-sm text-gray-900">
            Gratisträning
          </label>
        </div>

        {/* Bootcamp */}
        <div className="flex items-center">
          <input
            type="checkbox"
            id="bootcamp"
            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            {...register('bootcamp')}
          />
          <label htmlFor="bootcamp" className="ml-2 block text-sm text-gray-900">
            Bootcamp
          </label>
        </div>

        {/* Allow non-bootcamp join (only shown if bootcamp is enabled) */}
        {bootcampEnabled && (
          <div className="ml-6 flex items-center">
            <input
              type="checkbox"
              id="allow_non_bootcamp_join"
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              {...register('allow_non_bootcamp_join')}
            />
            <label htmlFor="allow_non_bootcamp_join" className="ml-2 block text-sm text-gray-700">
              Tillåt icke-bootcampare att anmäla sig
            </label>
          </div>
        )}
      </div>

      {/* Form Actions */}
      <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Avbryt
        </Button>
        <Button
          type="submit"
          disabled={isSubmitting}
          isLoading={isSubmitting}
        >
          {isSubmitting ? 'Skapar...' : 'Skapa träning'}
        </Button>
      </div>
    </form>
  )
}
